import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from "react";
import MapboxDraw, {
  DrawModeChangeEvent,
  MapboxDrawOptions,
} from "@mapbox/mapbox-gl-draw";
import {
  LngLat,
  LngLatLike,
  Point,
  useControl,
  useMap,
} from "react-map-gl/maplibre";
import MapLibre from "maplibre-gl";
import { ControlPosition } from "react-map-gl/maplibre";
import { Feature, FeatureCollection, MultiPolygon, Polygon } from "geojson";
import calculateArea from "@turf/area";
import calculateCentroid from "@turf/centroid";
import MapControl from "./MapControl";
import styles from "../Field/FieldMap.module.css";
import { useTranslation } from "react-i18next";
import useHistory from "./useHistory";
import "./Draw.css";
import { booleanIntersects, coordAll, featureEach, kinks } from "@turf/turf";
import { getThemeWithOverrides } from "./theme-utils";

type Info = {
  area: number;
  valid: boolean;
  centroid: {
    map: LngLat;
    screen: Point;
  };
};

export type DrawRenderControl = FunctionComponent<{
  info: Info | null;
  draw: MapboxDraw;
  save: () => void;
  cancel: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}>;

export type DrawRenderInfo = FunctionComponent<{
  info: Info;
}>;

export type DrawProps = {
  options?: MapboxDrawOptions;
  position?: ControlPosition;
  editPolygon?: Feature<Polygon | MultiPolygon> | null | undefined;
  renderControls?: DrawRenderControl;
  renderInfo?: DrawRenderInfo;
  onSave?: (features: FeatureCollection) => void;
  onCancel?: () => void;
  existingFields: FeatureCollection<MultiPolygon | Polygon>;
};

function defaultHandler() {
  // do nothing
}

export function isValidPolygon(
  feature: Feature<MultiPolygon | Polygon>,
  fields: FeatureCollection<MultiPolygon | Polygon>,
): boolean {
  return (
    coordAll(feature).length > 3 &&
    !kinks(feature).features.length &&
    !fields.features.some((f) => booleanIntersects(feature, f))
  );
}

export function DrawInfo({
  info,
}: Parameters<DrawRenderInfo>[0]): ReturnType<DrawRenderInfo> {
  const { t } = useTranslation();

  return (
    <div
      className={styles.tooltip}
      style={{
        left: info.centroid.screen.x + "px",
        top: info.centroid.screen.y + "px",
      }}
    >
      {t("text.{{area}}.ha", {
        area: (info.area / 10_000).toFixed(2),
      })}
    </div>
  );
}

export default function Draw({
  options = {},
  editPolygon,
  position = "top-right",
  renderControls = () => null,
  renderInfo = DrawInfo,
  onSave = defaultHandler,
  onCancel = defaultHandler,
  existingFields = { type: "FeatureCollection", features: [] },
}: DrawProps): React.ReactNode {
  const { current: map } = useMap();

  function createInfo(
    collection: FeatureCollection<Polygon | MultiPolygon>,
  ): Info | null {
    if (!map || !collection.features.length) {
      return null;
    }

    const centroid = MapLibre.LngLat.convert(
      calculateCentroid(collection).geometry.coordinates as LngLatLike,
    );
    return {
      area: calculateArea(collection),
      valid: isValidPolygon(collection.features[0], existingFields),
      centroid: {
        map: centroid,
        screen: map.project(centroid),
      },
    };
  }

  const [info, setInfo] = useState<Info | null>(
    createInfo({
      type: "FeatureCollection",
      features: editPolygon ? [editPolygon] : [],
    }),
  );

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const draw = useControl<MapboxDraw>(
    () => {
      return new MapboxDraw({
        displayControlsDefault: false,
        styles: getThemeWithOverrides(),
        keybindings: true,
        userProperties: true,
        ...options,
      });
    },
    { position },
  );

  const history = useHistory<FeatureCollection<Polygon | MultiPolygon>>(
    draw.set,
    {
      type: "FeatureCollection",
      features: editPolygon ? [editPolygon] : [],
    },
  );

  const save = useCallback(() => {
    onSave(draw.getAll());
  }, [draw, onSave]);

  useEffect(() => {
    const onUpdate = () => {
      const allFeatures = draw.getAll() as FeatureCollection<
        Polygon | MultiPolygon
      >;
      setInfo(createInfo(allFeatures));

      featureEach(allFeatures, (feature) => {
        feature.properties = {
          valid: isValidPolygon(feature, existingFields),
        };
      });
      history.edit(allFeatures);
    };

    const handleModeChange = (event: DrawModeChangeEvent) => {
      if (event.mode !== "draw_polygon" && !draw.getAll().features.length) {
        draw.changeMode("draw_polygon");
      }
    };

    const keyboardHandler = (event: KeyboardEvent) => {
      if (event.key === "Delete" || event.key === "Backspace") {
        draw.trash();
      }
      if (event.key == "Escape") {
        onCancel();
      }
      if (event.ctrlKey && event.key === "s") {
        event.preventDefault();
        onSave(draw.getAll());
      }
      if (event.ctrlKey && event.key === "z") {
        event.preventDefault();
        history.undo();
      }
      if (event.ctrlKey && event.key === "y") {
        event.preventDefault();
        history.redo();
      }
    };

    if (editPolygon) {
      draw.add(editPolygon);
      // The typescript definition in the library is wrong for the used version of typescript
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      draw.changeMode("direct_select", { featureId: editPolygon.id });
    } else {
      draw.changeMode("draw_polygon");
    }

    map?.on("draw.update", onUpdate);
    map?.on("draw.delete", onUpdate);
    map?.on("draw.create", onUpdate);
    map?.on("draw.modechange", handleModeChange);
    map?.getCanvas().addEventListener("keydown", keyboardHandler);

    return () => {
      map?.off("draw.update", onUpdate);
      map?.off("draw.delete", onUpdate);
      map?.off("draw.create", onUpdate);
      map?.off("draw.modechange", handleModeChange);
      map?.getCanvas().removeEventListener("keydown", keyboardHandler);
    };
  }, [map, draw]);

  return (
    <>
      {info && React.createElement(renderInfo, { info })}
      <MapControl position={position}>
        {React.createElement(renderControls, {
          info,
          draw,
          save,
          undo: history.undo,
          redo: history.redo,
          canRedo: history.canRedo,
          canUndo: history.canUndo,
          cancel: onCancel,
        })}
      </MapControl>
    </>
  );
}
